import React, {
	forwardRef,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';
import {useTranslation} from 'next-i18next';
import {useSearch} from '@deezer/next/shared/hooks/useSearch';

import type {LabelSearchResult} from '@deezer/next/services/api/Starmania/requests/search/types';
import type {Input} from '@tempo/core';
import {VStack, Skeleton, Text} from '@tempo/core';
import SearchInput from '@components/SearchInput';
import {ContentType} from '@deezer/next/services/types/contentType';

type InputProps = React.ComponentProps<typeof Input>;
type LabelSearchInputProps = {
	selectedLabel?: LabelSearchResult;
	selectedProviderId?: number;
	onSelectedLabelChange: (item?: LabelSearchResult) => void;
	filterLabelIds?: number[];
	handleClearUPCOnRemoveItem?: () => void;
} & InputProps;

export type LabelSearchInputRef = {
	setValue: (value: string) => void;
};

const LabelSearchInput = forwardRef<LabelSearchInputRef, LabelSearchInputProps>(
	(
		{
			selectedLabel,
			selectedProviderId,
			onSelectedLabelChange,
			filterLabelIds,
			handleClearUPCOnRemoveItem,
			...rest
		},
		ref,
	) => {
		const {t} = useTranslation('d4c');
		const [value, setValue] = useState(selectedLabel?.name || '');
		const {
			isSearching,
			results,
			error,
			search: searchLabel,
			setResults,
		} = useSearch(ContentType.Label);

		useImperativeHandle(ref, () => ({
			setValue: (value: string) => setValue(value),
		}));

		const handleChange: React.ChangeEventHandler<
			HTMLInputElement
		> = async e => {
			e.preventDefault();
			const searchValue = e.target.value;
			setValue(searchValue);
			onSelectedLabelChange(undefined);

			if (searchValue) {
				await searchLabel(searchValue, {providerId: selectedProviderId});
			} else {
				setResults(undefined);
			}
		};

		const handleResultClick = useCallback(
			(item: LabelSearchResult) => {
				onSelectedLabelChange(item);
				setValue(item.name);
			},
			[onSelectedLabelChange],
		);

		const handleSearchClear = useCallback(() => {
			setResults(undefined);
			if (handleClearUPCOnRemoveItem) {
				handleClearUPCOnRemoveItem();
			}
			setValue('');
			onSelectedLabelChange(undefined);
		}, [onSelectedLabelChange, setResults, handleClearUPCOnRemoveItem]);

		const renderSearchResult = useCallback(
			({name}: LabelSearchResult) => (
				<VStack w="100%" spacing={1} alignItems="flex-start" py={1}>
					<Text variant="body.default.regular">{name}</Text>
				</VStack>
			),
			[],
		);

		const renderSearchSkeleton = useCallback(
			() => (
				<VStack w="100%" spacing={1}>
					<Skeleton height={'14px'} width={'100%'} />
					<Skeleton height={'12px'} width={'100%'} />
				</VStack>
			),
			[],
		);

		const renderSearchPlaceholder = useCallback(
			() => (
				<Text textAlign="center" variant="body.default.regular" py={2} px={4}>
					{t('pitchingtoolform_text_nolabelmatch_d4c-web')}
				</Text>
			),
			[t],
		);

		const renderSearchError = useCallback(
			() => (
				<Text textAlign="center" variant="body.default.regular" py={2} px={4}>
					{t('pitchingtoolform_text_errorwhilesearchingforlabels_d4c-web')}
				</Text>
			),
			[t],
		);

		const filteredResults = useMemo(() => {
			if (!results || !filterLabelIds) {
				return results;
			}

			return results.filter(({id}) => !filterLabelIds.includes(id));
		}, [results, filterLabelIds]);

		return (
			<SearchInput
				value={value}
				error={error}
				onChange={handleChange}
				searchResults={filteredResults}
				isLoading={isSearching}
				renderSearchResult={renderSearchResult}
				renderSearchSkeleton={renderSearchSkeleton}
				renderSearchPlaceholder={renderSearchPlaceholder}
				renderSearchError={renderSearchError}
				searchResultKeyExtractor={({id}) => String(id)}
				onSelectResult={handleResultClick}
				autoComplete="off"
				onSearchClear={handleSearchClear}
				hideInputLeftElement={true}
				{...rest}
			/>
		);
	},
);

LabelSearchInput.displayName = 'LabelSearchInput';
export default LabelSearchInput;
